import React, { useState, useEffect } from "react";
import { Link, useLocation } from "react-router-dom";

const Header = () => {
  const [isMobileMenuOpen, setIsMobileMenuOpen] = useState(false);
  const [isServicesOpen, setIsServicesOpen] = useState(false);
  const [isScrolled, setIsScrolled] = useState(false);
  const location = useLocation();

  // Handle scroll
  useEffect(() => {
    const handleScroll = () => {
      if (window.scrollY > 0) {
        setIsScrolled(true);
      } else {
        setIsScrolled(false);
      }
    };

    window.addEventListener('scroll', handleScroll);
    return () => window.removeEventListener('scroll', handleScroll);
  }, []);

  // Close dropdown when clicking outside
  const handleClickOutside = (e) => {
    if (!e.target.closest('.dropdown-container')) {
      setIsServicesOpen(false);
    }
  };

  // Add event listener when dropdown is open
  useEffect(() => {
    if (isServicesOpen) {
      document.addEventListener('click', handleClickOutside);
    }
    return () => {
      document.removeEventListener('click', handleClickOutside);
    };
  }, [isServicesOpen]);

  // Close mobile menu when route changes
  useEffect(() => {
    setIsMobileMenuOpen(false);
  }, [location]);

  const toggleMobileMenu = () => {
    setIsMobileMenuOpen(!isMobileMenuOpen);
  };

  const toggleServicesMenu = () => {
    setIsServicesOpen(!isServicesOpen);
  };

  const isActive = (path) => {
    return location.pathname === path ? "border-b-2 border-emerald-400" : "";
  };

  const serviceLinks = [
    { title: "Global Coffee Export", path: "/service" },
    { title: "Strategic Sourcing", path: "/service" },
    { title: "Agro-Processing", path: "/service" },
    { title: "Agricultural Inputs", path: "/service" },
    { title: "Agro-Tech Innovation", path: "/service" }
  ];

  return (
    <nav className={`fixed top-0 left-0 right-0 z-50 transition-colors duration-300 ${isScrolled ? 'bg-gray-900/90 backdrop-blur-sm' : 'backdrop-blur-sm'}`}>
      <div className="container mx-auto px-4 py-3">
        <div className="flex items-center justify-between">
          {/* Logo */}
          <Link to="/" className="flex items-center space-x-2">
            <img src="/logo.svg" alt="Logo" className="h-10 w-auto" />
          </Link>

          {/* Desktop Navigation */}
          <div className="hidden md:flex items-center space-x-8">
            <Link
              to="/"
              className={`text-white hover:text-emerald-400 font-medium transition-colors duration-300 py-2 ${isActive("/")}`}
            >
              Home
            </Link>
            <Link
              to="/product"
              className={`text-white hover:text-emerald-400 font-medium transition-colors duration-300 py-2 ${isActive("/product")}`}
            >
              Products
            </Link>
            <Link
              to="/about"
              className={`text-white hover:text-emerald-400 font-medium transition-colors duration-300 py-2 ${isActive("/about")}`}
            >
              About
            </Link>

            {/* Services Dropdown */}
            <div className="relative dropdown-container">
              <button
                onClick={toggleServicesMenu}
                className={`text-white font-medium py-2 hover:text-emerald-400 flex items-center focus:outline-none transition-colors duration-300 ${isActive("/service")}`}
              >
                Services
                <svg
                  xmlns="http://www.w3.org/2000/svg"
                  className={`h-4 w-4 ml-2 transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`}
                  fill="none"
                  viewBox="0 0 24 24"
                  stroke="currentColor"
                >
                  <path
                    strokeLinecap="round"
                    strokeLinejoin="round"
                    strokeWidth={2}
                    d="M19 9l-7 7-7-7"
                  />
                </svg>
              </button>
              
              {isServicesOpen && (
                <div className="absolute w-56 bg-white rounded-lg shadow-xl py-2 mt-2 right-0 transform transition-all duration-300 ease-in-out">
                  {serviceLinks.map((service, index) => (
                    <Link
                      key={index}
                      to={service.path}
                      className="block px-4 py-3 text-gray-800 hover:bg-emerald-600 hover:text-white transition-colors duration-200"
                    >
                      {service.title}
                    </Link>
                  ))}
                </div>
              )}
            </div>

            <Link
              to="/contact"
              className={`text-white hover:text-emerald-400 font-medium transition-colors duration-300 py-2 ${isActive("/contact")}`}
            >
              Contact Us
            </Link>
            
          </div>

          {/* Mobile Menu Button */}
          <button
            onClick={toggleMobileMenu}
            className="md:hidden text-white focus:outline-none"
            aria-label="Toggle menu"
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d={isMobileMenuOpen ? "M6 18L18 6M6 6l12 12" : "M4 6h16M4 12h16M4 18h16"}
              />
            </svg>
          </button>
        </div>
      </div>

      {/* Mobile Menu */}
      <div
        className={`${
          isMobileMenuOpen ? "max-h-screen opacity-100" : "max-h-0 opacity-0"
        } md:hidden overflow-hidden transition-all duration-300 ease-in-out bg-emerald-800`}
      >
        <div className="container mx-auto px-4 py-2 space-y-1">
          <Link
            to="/"
            className="block py-3 px-4 text-white hover:bg-emerald-700 rounded-lg"
          >
            Home
          </Link>
          <Link
            to="/product"
            className="block py-3 px-4 text-white hover:bg-emerald-700 rounded-lg"
          >
            Products
          </Link>
          <Link
            to="/about"
            className="block py-3 px-4 text-white hover:bg-emerald-700 rounded-lg"
          >
            About
          </Link>
          
          {/* Mobile Services Accordion */}
          <div className="relative">
            <button
              onClick={() => setIsServicesOpen(!isServicesOpen)}
              className="w-full py-3 px-4 text-white hover:bg-emerald-700 rounded-lg flex items-center justify-between"
            >
              Services
              <svg
                xmlns="http://www.w3.org/2000/svg"
                className={`h-4 w-4 transition-transform duration-300 ${isServicesOpen ? 'rotate-180' : ''}`}
                fill="none"
                viewBox="0 0 24 24"
                stroke="currentColor"
              >
                <path
                  strokeLinecap="round"
                  strokeLinejoin="round"
                  strokeWidth={2}
                  d="M19 9l-7 7-7-7"
                />
              </svg>
            </button>
            
            <div className={`${isServicesOpen ? 'block' : 'hidden'} bg-emerald-900 rounded-lg mt-1`}>
              {serviceLinks.map((service, index) => (
                <Link
                  key={index}
                  to={service.path}
                  className="block py-3 px-8 text-emerald-100 hover:bg-emerald-700 hover:text-white"
                >
                  {service.title}
                </Link>
              ))}
            </div>
          </div>
          
          <Link
            to="/contact"
            className="block py-3 px-4 text-white hover:bg-emerald-700 rounded-lg"
          >
            Contact Us
          </Link>
          
          <Link
            to="/contact"
            className="block py-3 px-4 mt-2 mb-2 text-center bg-emerald-600 hover:bg-emerald-700 text-white rounded-lg"
          >
            Get Started
          </Link>
        </div>
      </div>
    </nav>
  );
};

export default Header;
